import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { api } from '@/lib/apiClient'
import { getQrCodeUrl } from '@/lib/qrCode'
import {
  BADGE_DESIGN_DPI,
  BADGE_FONT_FAMILIES,
  badgeCanvasSizeInPixels,
  createElementId,
  createDefaultBadgeTemplate,
  ensureTemplatePresetConsistency,
  normalizeBadgeTemplate,
  resolveElementText,
  resolveMergeValue,
  templateIsDoubleSided,
} from '@/lib/badgeTemplate'
import type {
  BadgeElement,
  BadgeMergeTag,
  BadgePrintData,
  BadgePrintEventData,
  BadgePrintRegistrant,
  BadgeTemplate,
  BadgeTextTransform,
} from '@/types/badgeDesigner'
import { Button, Input, InputNumber, Select, Tooltip, message } from '@/components/ui'
import { useBadgePrinterSelection } from '@/hooks/useBadgePrinterSelection'
import PrinterSelector from '@/components/badge_printing/PrinterSelector'
import { triggerRegistrantBadgePrint } from '@/lib/registrantBadgePrint'
import {
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons'

type UpdateTemplateResponse = {
  badgeTemplate?: BadgeTemplate
  message?: string
}

type InteractionState =
  | {
      mode: 'drag'
      elementId: string
      startMouseX: number
      startMouseY: number
      startX: number
      startY: number
    }
  | {
      mode: 'resize'
      elementId: string
      startMouseX: number
      startMouseY: number
      startWidth: number
      startHeight: number
    }

/**
 * Fields a properties-panel edit may set on the selected element. Distributes
 * over the BadgeElement union (dropping the `id`/`type` discriminants) so each
 * variant contributes its own keys with their real value types.
 */
type BadgeElementUpdate = BadgeElement extends infer Element
  ? Element extends BadgeElement
    ? Partial<Omit<Element, 'id' | 'type'>>
    : never
  : never

interface BadgeDesignTabProps {
  eventSlug: string
  eventData?: BadgePrintEventData
  badgeTemplate?: BadgeTemplate
  mergeTags: BadgeMergeTag[]
  registrantsForTest: BadgePrintRegistrant[]
}

type AutoFitTextBoxProps = {
  text: string
  fontFamily: string
  fontWeight: string
  textAlign: 'left' | 'center' | 'right'
  textTransform: BadgeTextTransform
  color: string
  maxFontSize: number
}

function AutoFitTextBox({
  text,
  fontFamily,
  fontWeight,
  textAlign,
  textTransform,
  color,
  maxFontSize,
}: AutoFitTextBoxProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let nextSize = Math.max(8, Math.round(maxFontSize))
    el.style.fontSize = `${nextSize}px`
    el.style.lineHeight = '1.1'

    while (nextSize > 8 && (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)) {
      nextSize -= 1
      el.style.fontSize = `${nextSize}px`
    }
  }, [text, fontFamily, fontWeight, textAlign, textTransform, color, maxFontSize])

  return (
    <div
      ref={ref}
      className="w-full h-full overflow-hidden whitespace-pre-wrap break-words"
      style={{
        fontFamily,
        fontWeight,
        textAlign,
        textTransform,
        color,
      }}
    >
      {text}
    </div>
  )
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof Error)) return fallback
  const candidate = error as Error & { data?: { errors?: string[] } }
  return candidate.data?.errors?.join(', ') || error.message || fallback
}

const elementTypeLabel = (element: BadgeElement) => {
  if (element.type === 'text') return 'Text'
  if (element.type === 'merge_tag') return `Merge: ${element.merge_key}`
  if (element.type === 'qr_code') return 'QR Code'
  if (element.type === 'image') return 'Image'
  return `Shape: ${element.shape_kind}`
}

const cloneTemplate = (template: BadgeTemplate): BadgeTemplate => ({
  ...template,
  front_elements: template.front_elements.map((element) => ({ ...element })),
  back_elements: template.back_elements.map((element) => ({ ...element })),
})

export default function BadgeDesignTab({
  eventSlug,
  eventData,
  badgeTemplate,
  mergeTags,
  registrantsForTest,
}: BadgeDesignTabProps) {
  const [template, setTemplate] = useState<BadgeTemplate>(() =>
    normalizeBadgeTemplate(badgeTemplate || createDefaultBadgeTemplate()),
  )
  const [activeSide, setActiveSide] = useState<'front' | 'back'>('front')
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null)
  const [interaction, setInteraction] = useState<InteractionState | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [selectedMergeTagKey, setSelectedMergeTagKey] = useState<string>('full_name')
  const [selectedRegistrantId, setSelectedRegistrantId] = useState<number | null>(
    registrantsForTest[0]?.id || null,
  )
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const canvasRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setTemplate(normalizeBadgeTemplate(badgeTemplate || createDefaultBadgeTemplate()))
  }, [badgeTemplate])

  useEffect(() => {
    if (!templateIsDoubleSided(template) && activeSide === 'back') {
      setActiveSide('front')
    }
  }, [template, activeSide])

  const {
    isLoadingPrinters,
    printerOptions,
    selectedPrinterUid,
    selectedPrinter,
    setSelectedPrinterUid,
    detectPrinters,
    hasActivePrinter,
    setupRequired,
    setupMessage,
  } = useBadgePrinterSelection(eventSlug, {
    enabled: true,
    autoDetect: true,
    notifyWhenEmpty: false,
  })

  const canvasSize = useMemo(
    () => badgeCanvasSizeInPixels(template, BADGE_DESIGN_DPI),
    [template.width_in, template.height_in],
  )

  const activeElements = useMemo(
    () => (activeSide === 'front' ? template.front_elements : template.back_elements),
    [template, activeSide],
  )

  const selectedElement = useMemo(
    () => activeElements.find((element) => element.id === selectedElementId) || null,
    [activeElements, selectedElementId],
  )

  const previewRegistrant = useMemo(
    () =>
      registrantsForTest.find((registrant) => registrant.id === selectedRegistrantId) ||
      registrantsForTest[0] ||
      null,
    [registrantsForTest, selectedRegistrantId],
  )

  useEffect(() => {
    if (!selectedElementId) return
    if (!activeElements.some((element) => element.id === selectedElementId)) {
      setSelectedElementId(null)
    }
  }, [activeElements, selectedElementId])

  const previewData = useMemo<BadgePrintData | undefined>(() => {
    if (!previewRegistrant) return undefined
    return {
      registrant: previewRegistrant,
      event: eventData,
    }
  }, [previewRegistrant, eventData])

  const setElementsForSide = (side: 'front' | 'back', elements: BadgeElement[]) => {
    setTemplate((prev) => ({
      ...prev,
      front_elements: side === 'front' ? elements : prev.front_elements,
      back_elements: side === 'back' ? elements : prev.back_elements,
    }))
  }

  const updateSelectedElement = (updates: BadgeElementUpdate) => {
    if (!selectedElementId) return
    setElementsForSide(
      activeSide,
      activeElements.map((element) =>
        element.id === selectedElementId ? ({ ...element, ...updates } as BadgeElement) : element,
      ),
    )
  }

  const appendElement = (element: BadgeElement) => {
    const nextElements = [...activeElements, element]
    setElementsForSide(activeSide, nextElements)
    setSelectedElementId(element.id)
  }

  const removeSelectedElement = () => {
    if (!selectedElementId) return
    setElementsForSide(
      activeSide,
      activeElements.filter((element) => element.id !== selectedElementId),
    )
    setSelectedElementId(null)
  }

  const addTextElement = () => {
    appendElement({
      id: createElementId(),
      type: 'text',
      text: 'Sample text',
      x: 26,
      y: 22 + activeElements.length * 10,
      width: 250,
      height: 70,
      font_family: 'Arial',
      font_size: 40,
      font_weight: '600',
      text_align: 'left',
      text_transform: 'none',
      color: '#111827',
    })
  }

  const addMergeTagElement = (mergeKey: string) => {
    appendElement({
      id: createElementId(),
      type: 'merge_tag',
      merge_key: mergeKey,
      x: 26,
      y: 22 + activeElements.length * 10,
      width: 250,
      height: 70,
      font_family: 'Arial',
      font_size: 40,
      font_weight: '600',
      text_align: 'left',
      text_transform: 'none',
      color: '#111827',
    })
  }

  const addQrElement = () => {
    appendElement({
      id: createElementId(),
      type: 'qr_code',
      x: 220,
      y: 380,
      width: 120,
      height: 120,
    })
  }

  const addShapeElement = () => {
    appendElement({
      id: createElementId(),
      type: 'shape',
      shape_kind: 'rectangle',
      x: 24,
      y: 220,
      width: 180,
      height: 70,
      stroke_color: '#111827',
      fill_color: '#ffffff',
      stroke_width: 2,
    })
  }

  const handleImagePick = () => imageInputRef.current?.click()

  const handleImageFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : ''
      if (!src) return
      appendElement({
        id: createElementId(),
        type: 'image',
        src,
        x: 24,
        y: 300,
        width: 180,
        height: 120,
      })
    }
    reader.readAsDataURL(file)
    event.target.value = ''
  }

  const handleTemplateSave = async () => {
    setIsSaving(true)
    try {
      const payloadTemplate = cloneTemplate(ensureTemplatePresetConsistency(template))
      const response = await api.patch<UpdateTemplateResponse>(
        `/events/${eventSlug}/badge_printings/update_template`,
        { badge_template: payloadTemplate },
      )
      if (response.badgeTemplate) {
        setTemplate(normalizeBadgeTemplate(response.badgeTemplate))
      }
      message.success(response.message || 'Badge template saved')
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'Failed to save template'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleTestPrint = async () => {
    const registrant = previewRegistrant
    if (!registrant) {
      message.warning('No registrant available for test print')
      return
    }

    if (!selectedPrinterUid || !hasActivePrinter) {
      message.warning(setupMessage || 'Pair a print station before test printing')
      return
    }

    const printerName = selectedPrinter?.printerName || selectedPrinter?.label

    setIsPrinting(true)
    try {
      await triggerRegistrantBadgePrint({
        eventSlug,
        registrantId: registrant.id,
        printerUid: selectedPrinter?.printerUid || selectedPrinterUid,
        printerId: selectedPrinter?.printerId,
        stationId: selectedPrinter?.stationId,
        printStationPrinterId: selectedPrinter?.printStationPrinterId,
        printStationId: selectedPrinter?.printStationId,
        printerProfileId: selectedPrinter?.printerProfileId,
        printerName,
        template: ensureTemplatePresetConsistency(template),
        data: {
          registrant,
          event: eventData,
        },
        source: 'badge_designer_test',
      })
      message.success('Test print job queued')
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'Failed to queue test print'))
    } finally {
      setIsPrinting(false)
    }
  }

  const startDrag = (event: React.MouseEvent, element: BadgeElement) => {
    event.preventDefault()
    event.stopPropagation()
    setSelectedElementId(element.id)
    setInteraction({
      mode: 'drag',
      elementId: element.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      startX: element.x,
      startY: element.y,
    })
  }

  const startResize = (event: React.MouseEvent, element: BadgeElement) => {
    event.preventDefault()
    event.stopPropagation()
    setSelectedElementId(element.id)
    setInteraction({
      mode: 'resize',
      elementId: element.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      startWidth: element.width,
      startHeight: element.height,
    })
  }

  useEffect(() => {
    if (!interaction) return

    const onMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - interaction.startMouseX
      const deltaY = event.clientY - interaction.startMouseY

      setTemplate((prev) => {
        const key = activeSide === 'front' ? 'front_elements' : 'back_elements'
        const elements = [...prev[key]]
        const index = elements.findIndex((item) => item.id === interaction.elementId)
        if (index < 0) return prev

        const current = elements[index]
        if (interaction.mode === 'drag') {
          const nextX = clamp(interaction.startX + deltaX, 0, canvasSize.width - current.width)
          const nextY = clamp(interaction.startY + deltaY, 0, canvasSize.height - current.height)
          elements[index] = { ...current, x: nextX, y: nextY }
        } else {
          const nextWidth = clamp(interaction.startWidth + deltaX, 16, canvasSize.width - current.x)
          const nextHeight = clamp(
            interaction.startHeight + deltaY,
            16,
            canvasSize.height - current.y,
          )
          elements[index] = { ...current, width: nextWidth, height: nextHeight }
        }

        return {
          ...prev,
          [key]: elements,
        }
      })
    }

    const onMouseUp = () => setInteraction(null)

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [interaction, activeSide, canvasSize.width, canvasSize.height])

  const handlePresetChange = (preset: BadgeTemplate['preset']) => {
    if (preset === '4x6_single') {
      setTemplate((prev) => ({
        ...prev,
        preset,
        width_in: 4,
        height_in: 6,
        layout_mode: 'single_sided',
        back_elements: [],
      }))
      setActiveSide('front')
      return
    }

    if (preset === '4x6_double') {
      setTemplate((prev) => ({
        ...prev,
        preset,
        width_in: 4,
        height_in: 6,
        layout_mode: 'double_sided',
      }))
      return
    }

    setTemplate((prev) => ({ ...prev, preset }))
  }

  const handleLayoutModeChange = (layoutMode: BadgeTemplate['layout_mode']) => {
    setTemplate((prev) => ({
      ...prev,
      layout_mode: layoutMode,
      back_elements: layoutMode === 'double_sided' ? prev.back_elements : [],
    }))
  }

  const cloneFrontToBack = () => {
    setTemplate((prev) => ({
      ...prev,
      back_elements: prev.front_elements.map((element) => ({
        ...element,
        id: createElementId(),
      })),
    }))
    setActiveSide('back')
    setSelectedElementId(null)
    message.success('Front view cloned to back view')
  }

  const renderElementPreview = (element: BadgeElement) => {
    if (element.type === 'text') {
      return (
        <AutoFitTextBox
          text={resolveElementText(element, previewData)}
          fontFamily={element.font_family}
          fontWeight={element.font_weight}
          textAlign={element.text_align}
          textTransform={element.text_transform}
          color={element.color}
          maxFontSize={element.font_size}
        />
      )
    }

    if (element.type === 'merge_tag') {
      const value = resolveElementText(element, previewData) || `{{${element.merge_key}}}`
      return (
        <AutoFitTextBox
          text={value}
          fontFamily={element.font_family}
          fontWeight={element.font_weight}
          textAlign={element.text_align}
          textTransform={element.text_transform}
          color={element.color}
          maxFontSize={element.font_size}
        />
      )
    }

    if (element.type === 'qr_code') {
      const qrValue = resolveMergeValue('qr_value', previewData)
      const src = qrValue
        ? getQrCodeUrl(qrValue, { size: Math.max(element.width, element.height) })
        : ''
      return src ? (
        <img src={src} alt="QR" className="w-full h-full object-contain" />
      ) : (
        <div className="w-full h-full grid place-items-center text-xs text-gray-500">QR</div>
      )
    }

    if (element.type === 'image') {
      return element.src ? (
        <img src={element.src} alt="Badge" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full grid place-items-center text-xs text-gray-500">Image</div>
      )
    }

    const borderWidth = Math.max(1, element.stroke_width)
    if (element.shape_kind === 'line') {
      return (
        <svg className="w-full h-full">
          <line
            x1="0"
            y1="0"
            x2="100%"
            y2="100%"
            stroke={element.stroke_color}
            strokeWidth={borderWidth}
          />
        </svg>
      )
    }

    return (
      <div
        className={`w-full h-full ${element.shape_kind === 'ellipse' ? 'rounded-full' : ''}`}
        style={{
          border: `${borderWidth}px solid ${element.stroke_color}`,
          backgroundColor: element.fill_color,
        }}
      />
    )
  }

  const registrantOptions = registrantsForTest.map((registrant) => ({
    label: `${registrant.full_name || registrant.email || `Registrant #${registrant.id}`} (${registrant.email || 'no-email'})`,
    value: registrant.id,
  }))

  const mergeTagOptions = mergeTags.map((tag) => ({
    label: tag.label,
    value: tag.key,
  }))

  return (
    <div className="grid gap-4 xl:grid-cols-[400px_1fr]">
      <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold m-0">Badge Setup</h3>
          <Button
            variant="primary"
            icon={<SaveOutlined />}
            loading={isSaving}
            onClick={handleTemplateSave}
          >
            Save
          </Button>
        </div>

        <div className="space-y-2">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Preset</label>
            <Select
              value={template.preset}
              options={[
                { label: '4 x 6 (Single-Sided)', value: '4x6_single' },
                { label: '4 x 6 (Double-Sided)', value: '4x6_double' },
                { label: 'Custom', value: 'custom' },
              ]}
              onChange={(value) => handlePresetChange(value as BadgeTemplate['preset'])}
            />
          </div>
        </div>

        {template.preset === 'custom' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Width (in)</label>
                <InputNumber
                  min={1}
                  max={20}
                  step={0.1}
                  value={template.width_in}
                  onChange={(value) =>
                    setTemplate((prev) => ({
                      ...prev,
                      width_in: typeof value === 'number' ? value : prev.width_in,
                    }))
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Height (in)</label>
                <InputNumber
                  min={1}
                  max={20}
                  step={0.1}
                  value={template.height_in}
                  onChange={(value) =>
                    setTemplate((prev) => ({
                      ...prev,
                      height_in: typeof value === 'number' ? value : prev.height_in,
                    }))
                  }
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Sides</label>
              <Select
                value={template.layout_mode}
                options={[
                  { label: 'Single-Sided', value: 'single_sided' },
                  { label: 'Double-Sided', value: 'double_sided' },
                ]}
                onChange={(value) => handleLayoutModeChange(value as BadgeTemplate['layout_mode'])}
              />
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-gray-200 space-y-2">
          <h4 className="text-sm font-semibold m-0">Insert Component</h4>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" icon={<PlusOutlined />} onClick={addTextElement}>
              Text
            </Button>
            <Button variant="secondary" icon={<PlusOutlined />} onClick={addQrElement}>
              QR Code
            </Button>
            <Button variant="secondary" icon={<PlusOutlined />} onClick={addShapeElement}>
              Shape
            </Button>
            <Button variant="secondary" icon={<UploadOutlined />} onClick={handleImagePick}>
              Image
            </Button>
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageFileChange}
          />
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
            <Select
              className="min-w-0"
              value={selectedMergeTagKey}
              options={mergeTagOptions}
              onChange={(value) => setSelectedMergeTagKey(value)}
            />
            <Button variant="secondary" onClick={() => addMergeTagElement(selectedMergeTagKey)}>
              Add Tag
            </Button>
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold m-0">Layers</h4>
            <Tooltip title="Delete selected element">
              <Button
                variant="text"
                icon={<DeleteOutlined />}
                onClick={removeSelectedElement}
                disabled={!selectedElementId}
                danger
              >
                Delete
              </Button>
            </Tooltip>
          </div>
          <div className="max-h-36 overflow-y-auto border border-gray-200 rounded p-2 space-y-1">
            {activeElements.length === 0 && (
              <p className="text-xs text-gray-500 m-0">No elements yet</p>
            )}
            {activeElements.map((element, index) => (
              <button
                key={element.id}
                type="button"
                className={`w-full text-left px-2 py-1 rounded text-xs ${
                  selectedElementId === element.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setSelectedElementId(element.id)}
              >
                {index + 1}. {elementTypeLabel(element)}
              </button>
            ))}
          </div>
        </div>

        {selectedElement && (
          <div className="pt-2 border-t border-gray-200 space-y-2">
            <h4 className="text-sm font-semibold m-0">Properties</h4>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">X</label>
                <InputNumber
                  value={selectedElement.x}
                  min={0}
                  max={canvasSize.width}
                  onChange={(value) =>
                    updateSelectedElement({
                      x: typeof value === 'number' ? value : selectedElement.x,
                    })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Y</label>
                <InputNumber
                  value={selectedElement.y}
                  min={0}
                  max={canvasSize.height}
                  onChange={(value) =>
                    updateSelectedElement({
                      y: typeof value === 'number' ? value : selectedElement.y,
                    })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Width</label>
                <InputNumber
                  value={selectedElement.width}
                  min={16}
                  max={canvasSize.width}
                  onChange={(value) =>
                    updateSelectedElement({
                      width: typeof value === 'number' ? value : selectedElement.width,
                    })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Height</label>
                <InputNumber
                  value={selectedElement.height}
                  min={16}
                  max={canvasSize.height}
                  onChange={(value) =>
                    updateSelectedElement({
                      height: typeof value === 'number' ? value : selectedElement.height,
                    })
                  }
                  className="w-full"
                />
              </div>
            </div>

            {(selectedElement.type === 'text' || selectedElement.type === 'merge_tag') && (
              <div className="space-y-2">
                {selectedElement.type === 'text' && (
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Text</label>
                    <Input
                      value={selectedElement.text}
                      onChange={(event) =>
                        updateSelectedElement({
                          text: event.target.value,
                        })
                      }
                    />
                  </div>
                )}

                {selectedElement.type === 'merge_tag' && (
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Merge Tag</label>
                    <Select
                      value={selectedElement.merge_key}
                      options={mergeTagOptions}
                      onChange={(value) =>
                        updateSelectedElement({
                          merge_key: value,
                        })
                      }
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Font</label>
                    <Select
                      value={selectedElement.font_family}
                      options={BADGE_FONT_FAMILIES.map((font) => ({ label: font, value: font }))}
                      onChange={(value) =>
                        updateSelectedElement({
                          font_family: value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Size</label>
                    <InputNumber
                      value={selectedElement.font_size}
                      min={8}
                      max={200}
                      onChange={(value) =>
                        updateSelectedElement({
                          font_size: typeof value === 'number' ? value : selectedElement.font_size,
                        })
                      }
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Weight</label>
                    <Select
                      value={selectedElement.font_weight}
                      options={[
                        { label: '400', value: '400' },
                        { label: '500', value: '500' },
                        { label: '600', value: '600' },
                        { label: '700', value: '700' },
                        { label: '800', value: '800' },
                      ]}
                      onChange={(value) =>
                        updateSelectedElement({
                          font_weight: value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Align</label>
                    <Select
                      value={selectedElement.text_align}
                      options={[
                        { label: 'Left', value: 'left' },
                        { label: 'Center', value: 'center' },
                        { label: 'Right', value: 'right' },
                      ]}
                      onChange={(value) =>
                        updateSelectedElement({
                          text_align: value as 'left' | 'center' | 'right',
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Case</label>
                    <Select
                      value={selectedElement.text_transform}
                      options={[
                        { label: 'Normal', value: 'none' },
                        { label: 'ALL CAPS', value: 'uppercase' },
                        { label: 'Capitalize', value: 'capitalize' },
                      ]}
                      onChange={(value) =>
                        updateSelectedElement({
                          text_transform: value as BadgeTextTransform,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Color</label>
                  <input
                    type="color"
                    value={selectedElement.color || '#111827'}
                    onChange={(event) =>
                      updateSelectedElement({
                        color: event.target.value,
                      })
                    }
                    className="w-full h-8 border border-gray-200 rounded"
                  />
                </div>
              </div>
            )}

            {selectedElement.type === 'image' && (
              <div>
                <label className="text-xs text-gray-600 block mb-1">Image URL / Data URL</label>
                <Input
                  value={selectedElement.src}
                  onChange={(event) => updateSelectedElement({ src: event.target.value })}
                />
              </div>
            )}

            {selectedElement.type === 'shape' && (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Shape</label>
                  <Select
                    value={selectedElement.shape_kind}
                    options={[
                      { label: 'Rectangle', value: 'rectangle' },
                      { label: 'Ellipse', value: 'ellipse' },
                      { label: 'Line', value: 'line' },
                    ]}
                    onChange={(value) =>
                      updateSelectedElement({
                        shape_kind: value as 'rectangle' | 'ellipse' | 'line',
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Stroke</label>
                    <input
                      type="color"
                      value={selectedElement.stroke_color}
                      onChange={(event) =>
                        updateSelectedElement({
                          stroke_color: event.target.value,
                        })
                      }
                      className="w-full h-8 border border-gray-200 rounded"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Fill</label>
                    <input
                      type="color"
                      value={selectedElement.fill_color}
                      onChange={(event) =>
                        updateSelectedElement({
                          fill_color: event.target.value,
                        })
                      }
                      className="w-full h-8 border border-gray-200 rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Stroke Width</label>
                  <InputNumber
                    value={selectedElement.stroke_width}
                    min={0}
                    max={20}
                    onChange={(value) =>
                      updateSelectedElement({
                        stroke_width:
                          typeof value === 'number' ? value : selectedElement.stroke_width,
                      })
                    }
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pt-2 border-t border-gray-200 space-y-2">
          <h4 className="text-sm font-semibold m-0">Queue Test Print</h4>
          <div>
            <label className="text-xs text-gray-600 block mb-1">Registrant</label>
            <Select
              disabled={registrantOptions.length === 0}
              value={selectedRegistrantId || undefined}
              options={registrantOptions}
              placeholder="No registrant available"
              onChange={(value) => setSelectedRegistrantId(Number(value))}
            />
            {registrantOptions.length === 0 && (
              <p className="text-xs text-amber-600 mt-1 mb-0">
                No registrants yet. Add at least one attendee before test printing.
              </p>
            )}
          </div>
          <PrinterSelector
            selectedPrinterUid={selectedPrinterUid}
            printerOptions={printerOptions}
            isLoadingPrinters={isLoadingPrinters}
            onPrinterChange={(value) => setSelectedPrinterUid(value)}
            onRefresh={() => detectPrinters({ notifyOnSuccess: true, notifyWhenEmpty: true })}
            setupRequired={setupRequired}
            setupMessage={setupMessage}
          />
          <Button
            block
            variant="primary"
            icon={<PrinterOutlined />}
            loading={isPrinting}
            onClick={handleTestPrint}
            disabled={!previewRegistrant || !hasActivePrinter || setupRequired}
          >
            Queue Test Print
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold m-0">Badge Canvas</h3>
          </div>
          {templateIsDoubleSided(template) && (
            <div className="flex items-center gap-2">
              <Button
                variant="tertiary"
                size="small"
                icon={<CopyOutlined />}
                onClick={cloneFrontToBack}
              >
                Clone Front to Back
              </Button>
              <div className="inline-flex rounded border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setActiveSide('front')}
                  className={`px-3 py-1 text-xs ${activeSide === 'front' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
                >
                  Front
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSide('back')}
                  className={`px-3 py-1 text-xs border-l border-gray-300 ${activeSide === 'back' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-auto bg-gray-100 rounded-md p-6 border border-gray-200">
          <div
            ref={canvasRef}
            className="relative bg-white shadow select-none"
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              minWidth: canvasSize.width,
              minHeight: canvasSize.height,
            }}
            onMouseDown={() => setSelectedElementId(null)}
          >
            {activeElements.map((element) => (
              <div
                key={element.id}
                className={`absolute cursor-move ${
                  selectedElementId === element.id
                    ? 'ring-2 ring-blue-500'
                    : 'ring-1 ring-transparent'
                }`}
                style={{
                  left: element.x,
                  top: element.y,
                  width: element.width,
                  height: element.height,
                }}
                onMouseDown={(event) => startDrag(event, element)}
                onClick={(event) => {
                  event.stopPropagation()
                  setSelectedElementId(element.id)
                }}
              >
                {renderElementPreview(element)}
                {selectedElementId === element.id && (
                  <button
                    type="button"
                    className="absolute -right-2 -bottom-2 w-4 h-4 rounded-sm bg-blue-600 border border-white cursor-se-resize"
                    onMouseDown={(event) => startResize(event, element)}
                    aria-label="Resize"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
